import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiEye, FiShoppingBag, FiDownload } from "react-icons/fi";
import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { useContext } from "react";
import { AuthContext } from "../../../Context/AuthContext";
import useAllOrders from "../../../marketplace_admin/libs/useAllOrders";
import useUpdateOrderStatus from "../../../Routes/Marketplace/libs/useUpdateOrderStatus";
import useMarkCodRefused from "../../../Routes/Marketplace/libs/useMarkCodRefused";
import OrderPaymentBadge from "../../../Routes/Marketplace/components/OrderPaymentBadge";
import {
  formatNgn,
  isPayOnDeliveryOrder,
  STATUS_STEPS,
} from "../../../Routes/Marketplace/libs/orderDisplayUtils";
import PageHeader from "../../components/PageHeader";
import FilterBar from "../../components/FilterBar";
import DataTable from "../../components/DataTable";
import EmptyState from "../../components/EmptyState";
import StatCard from "../../components/StatCard";
import StatusBadge from "../../components/StatusBadge";
import DetailDrawer from "../../components/DetailDrawer";
import DetailGrid from "../../components/DetailGrid";
import Spinner from "../../../Spinner/Spinner";
import { exportOrdersCsv } from "../utils/exportOrdersCsv";

const RETURN_STATUSES = ["pending", "approved", "rejected", "resolved"];

const STATUS_OPTIONS = ["Pending", "Processing", "Shipped", "Delivered", "Canceled"];

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  ...STATUS_OPTIONS.map((s) => ({ id: s, label: s })),
];

const getCustomerName = (order) => {
  const fromUser = [order.user?.first_name, order.user?.last_name].filter(Boolean).join(" ");
  return fromUser || order.shippingAddress?.full_name || "Guest";
};

const AdminOrdersPage = () => {
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { token } = useContext(AuthContext);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, isFetching } = useAllOrders(page, statusFilter);
  const { mutate: updateStatus, isLoading: updating } = useUpdateOrderStatus();
  const { mutate: markCodRefused, isLoading: refusing } = useMarkCodRefused();

  const { data: orderDetail } = useQuery(
    ["adminOrderDetail", selectedOrder?._id],
    async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/order/orders/${selectedOrder._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data;
    },
    { enabled: !!selectedOrder?._id && !!token }
  );

  const returnRequest = orderDetail?.returnRequest;

  const orders = useMemo(() => data?.orders || [], [data?.orders]);
  const totalPages = data?.totalPages || 1;
  const totalOrders = data?.totalOrders ?? orders.length;

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return orders;
    return orders.filter((order) => {
      const customer = getCustomerName(order).toLowerCase();
      return (
        String(order.orderNumber || "").toLowerCase().includes(term) ||
        customer.includes(term) ||
        (order.user?.email || "").toLowerCase().includes(term) ||
        (order.shippingAddress?.phone_number || "").includes(term)
      );
    });
  }, [orders, search]);

  const pageStats = useMemo(
    () => ({
      pending: orders.filter((o) => o.status === "Pending").length,
      processing: orders.filter((o) => o.status === "Processing").length,
      shipped: orders.filter((o) => o.status === "Shipped").length,
      delivered: orders.filter((o) => o.status === "Delivered").length,
    }),
    [orders]
  );

  const currentStep = selectedOrder ? STATUS_STEPS.indexOf(selectedOrder.status) : -1;

  const handleStatusChange = (orderId, status) => {
    updateStatus(
      { orderId, status },
      {
        onSuccess: () => {
          toast.success("Order updated");
          setSelectedOrder((prev) => (prev && prev._id === orderId ? { ...prev, status } : prev));
        },
        onError: (err) => toast.error(err.response?.data?.message || "Update failed"),
      }
    );
  };

  const canRecordRefusal = (order) =>
    isPayOnDeliveryOrder(order) &&
    order.paymentStatus === "pending" &&
    ["Processing", "Shipped"].includes(order.status);

  const handleCodRefusal = (order) => {
    if (!window.confirm("Record customer refused delivery? Stock will be restored and POD blocked.")) return;
    markCodRefused(order._id, {
      onSuccess: (payload) => {
        toast.success(payload.message || "Refusal recorded");
        setSelectedOrder(payload.order);
      },
      onError: (err) => toast.error(err.response?.data?.message || "Could not record refusal"),
    });
  };

  const handleExport = () => {
    const ok = exportOrdersCsv(filtered, `stex-orders-${new Date().toISOString().slice(0, 10)}.csv`);
    if (ok) toast.success(`Exported ${filtered.length} orders`);
    else toast.error("No orders to export");
  };

  const handleRefund = async (orderId) => {
    if (!window.confirm("Process refund for this order?")) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/order/orders/${orderId}/refund`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Refund processed");
      queryClient.invalidateQueries(["adminOrderDetail", orderId]);
      setSelectedOrder((prev) => (prev ? { ...prev, paymentStatus: "refunded" } : prev));
    } catch (err) {
      toast.error(err.response?.data?.message || "Refund failed");
    }
  };

  const handleReturnStatus = async (returnId, status) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_SERVER_URL}/order/returns/${returnId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Return request updated");
      queryClient.invalidateQueries(["adminOrderDetail", selectedOrder?._id]);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not update return");
    }
  };

  const columns = [
    {
      key: "orderNumber",
      label: "Order",
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600 }}>#{row.orderNumber}</div>
          <div style={{ fontSize: 12, color: "var(--dash-muted)" }}>
            {new Date(row.createdAt).toLocaleString()}
          </div>
        </div>
      ),
    },
    {
      key: "customer",
      label: "Customer",
      render: (row) => (
        <div>
          <div>{getCustomerName(row)}</div>
          {row.isGuest && (
            <span className="dashboard-badge dashboard-badge--neutral" style={{ marginTop: 4 }}>
              Guest
            </span>
          )}
        </div>
      ),
    },
    {
      key: "store",
      label: "Store",
      render: (row) => row.merchantStoreName || "—",
    },
    {
      key: "total",
      label: "Total",
      render: (row) => formatNgn(row.totalAmount),
    },
    {
      key: "payment",
      label: "Payment",
      render: (row) => <OrderPaymentBadge order={row} />,
    },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="dashboard-table-actions">
          <button
            type="button"
            className="dashboard-btn dashboard-btn--secondary"
            onClick={() => setSelectedOrder(row)}
          >
            <FiEye aria-hidden="true" /> View
          </button>
          <select
            className="dashboard-select-inline"
            value={row.status}
            disabled={updating}
            onChange={(e) => handleStatusChange(row._id, e.target.value)}
            aria-label={`Update status for order ${row.orderNumber}`}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Orders"
        subtitle="Track marketplace orders, payments, and fulfillment"
        icon={FiShoppingBag}
        actions={
          <button
            type="button"
            className="dashboard-btn dashboard-btn--secondary"
            onClick={handleExport}
            disabled={filtered.length === 0}
          >
            <FiDownload aria-hidden="true" /> Export CSV
          </button>
        }
      />

      {!isLoading && (
        <div className="dashboard-stat-grid">
          <StatCard label="Total orders" value={totalOrders} />
          <StatCard label="Pending (this page)" value={pageStats.pending} />
          <StatCard label="Processing (this page)" value={pageStats.processing} />
          <StatCard label="Shipped (this page)" value={pageStats.shipped} />
          <StatCard label="Delivered (this page)" value={pageStats.delivered} />
        </div>
      )}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search order #, customer, email, or phone…"
      >
        <div className="dashboard-filter-pills">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={statusFilter === f.id ? "active" : ""}
              onClick={() => {
                setStatusFilter(f.id);
                setPage(1);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </FilterBar>

      {isLoading && <Spinner />}
      {isError && (
        <div className="dashboard-card">
          <p style={{ color: "var(--dash-danger)", margin: 0 }}>Could not load orders.</p>
        </div>
      )}

      {!isLoading && !isError && filtered.length === 0 && (
        <EmptyState
          icon={FiShoppingBag}
          title="No orders found"
          message={
            search || statusFilter !== "all"
              ? "Try a different search or status filter."
              : "Orders will appear here when customers checkout on the marketplace."
          }
        />
      )}

      {filtered.length > 0 && (
        <>
          <DataTable
            columns={columns}
            rows={filtered.map((order) => ({ ...order, _key: order._id }))}
            loading={isLoading}
          />
          {totalPages > 1 && (
            <div className="admin-orders-pagination">
              <button
                type="button"
                className="dashboard-btn dashboard-btn--secondary"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span style={{ color: "var(--dash-muted)", fontSize: 14 }}>
                Page {page} of {totalPages}
                {isFetching ? " · Loading…" : ""}
              </span>
              <button
                type="button"
                className="dashboard-btn dashboard-btn--secondary"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      <DetailDrawer
        open={Boolean(selectedOrder)}
        title={selectedOrder ? `Order #${selectedOrder.orderNumber}` : "Order"}
        onClose={() => setSelectedOrder(null)}
      >
        {selectedOrder && (
          <div className="admin-order-detail">
            <div className="admin-order-detail__badges">
              <StatusBadge status={selectedOrder.status} />
              <OrderPaymentBadge order={selectedOrder} showDue />
              {selectedOrder.isGuest && (
                <span className="dashboard-badge dashboard-badge--neutral">Guest checkout</span>
              )}
            </div>

            {currentStep >= 0 && (
              <div className="admin-order-stepper">
                {STATUS_STEPS.map((step, i) => (
                  <div
                    key={step}
                    className={`admin-order-stepper__step${i <= currentStep ? " admin-order-stepper__step--active" : ""}${i < currentStep ? " admin-order-stepper__step--done" : ""}`}
                  >
                    <span className="admin-order-stepper__dot" />
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            )}

            <DetailGrid
              items={[
                { label: "Total", value: formatNgn(selectedOrder.totalAmount) },
                { label: "Placed", value: new Date(selectedOrder.createdAt).toLocaleString() },
                {
                  label: "Shipping method",
                  value: selectedOrder.shippingMethod === "pickup" ? "Pickup station" : "Door delivery",
                },
                { label: "Customer", value: getCustomerName(selectedOrder) },
                { label: "Phone", value: selectedOrder.shippingAddress?.phone_number || "—" },
                { label: "Email", value: selectedOrder.shippingAddress?.email || selectedOrder.user?.email || "—" },
              ]}
            />

            <section className="dashboard-card" style={{ marginTop: 16 }}>
              <h3 className="dashboard-card__title">Delivery address</h3>
              <p style={{ margin: 0, lineHeight: 1.6 }}>
                {selectedOrder.shippingAddress?.address}
                <br />
                {selectedOrder.shippingAddress?.city}, {selectedOrder.shippingAddress?.state}
              </p>
            </section>

            <section className="dashboard-card">
              <h3 className="dashboard-card__title">Items</h3>
              <ul className="admin-order-items">
                {(selectedOrder.items || []).map((item) => (
                  <li key={item._id || item.product?._id || item.name}>
                    <span>{item.name}</span>
                    <span>
                      ×{item.quantity} · {formatNgn((item.unitPrice || 0) * (item.quantity || 1))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            {returnRequest && (
              <section className="dashboard-card dashboard-return-panel">
                <h3 className="dashboard-card__title">Return request</h3>
                <p className="dashboard-return-panel__reason">{returnRequest.reason}</p>
                <p className="dashboard-form-hint">
                  Status: <strong>{returnRequest.status}</strong>
                  {returnRequest.adminNote ? ` — ${returnRequest.adminNote}` : ""}
                </p>
                <div className="dashboard-form-actions">
                  {RETURN_STATUSES.filter((s) => s !== returnRequest.status).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`dashboard-btn dashboard-btn--${s === "rejected" ? "danger" : "secondary"}`}
                      onClick={() => handleReturnStatus(returnRequest._id, s)}
                    >
                      Mark {s}
                    </button>
                  ))}
                </div>
              </section>
            )}

            <div className="admin-order-detail__actions">
              <label className="dashboard-form-field">
                <span>Update status</span>
                <select
                  value={selectedOrder.status}
                  disabled={updating}
                  onChange={(e) => handleStatusChange(selectedOrder._id, e.target.value)}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>

              {canRecordRefusal(selectedOrder) && (
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn--danger"
                  disabled={refusing}
                  onClick={() => handleCodRefusal(selectedOrder)}
                >
                  {refusing ? "Recording…" : "Customer refused delivery"}
                </button>
              )}

              {selectedOrder.paymentStatus !== "refunded" && selectedOrder.paymentStatus === "paid" && (
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn--danger"
                  onClick={() => handleRefund(selectedOrder._id)}
                >
                  Process refund
                </button>
              )}
            </div>
          </div>
        )}
      </DetailDrawer>
    </>
  );
};

export default AdminOrdersPage;
